// {CC 03/12/2026} Canvas-based voice aura visualizer ported from Ops-Voice-Aura design
import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import bgImageSrc from './bg-gradient.png';

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function easeInOutSine(t) {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

const VoiceVisualizer = forwardRef(({ state: externalState, externalAudioLevel, fillScreen = false }, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animFrameRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const smoothedLevelRef = useRef(0);
  const timeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const [internalState, setInternalState] = useState('idle');
  const internalAudioLevelRef = useRef(0);
  const canvasSizeRef = useRef({ w: 800, h: 600 });
  const dotWavesRef = useRef([]);
  const lastPeakRef = useRef(0);
  const prevLevelRef = useRef(0);
  const bgImageRef = useRef(null);

  const currentState = externalState ?? internalState;

  const startMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;
      setInternalState('listening');
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  }, []);

  const connectAnalyserToStream = useCallback((stream) => {
    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;
    } catch (err) {
      console.error('Failed to connect analyser:', err);
    }
  }, []);

  const stopMic = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setInternalState('idle');
  }, []);

  const disconnectAnalyser = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const setAudioLevel = useCallback((level) => {
    internalAudioLevelRef.current = Math.max(0, Math.min(1, level));
  }, []);

  useImperativeHandle(ref, () => ({
    startMic,
    stopMic,
    setState: setInternalState,
    setAudioLevel,
    connectAnalyserToStream,
    disconnectAnalyser,
  }));

  const getAudioLevel = useCallback(() => {
    if (externalAudioLevel !== undefined) {
      return externalAudioLevel;
    }
    if (currentState === 'speaking') {
      return internalAudioLevelRef.current;
    }
    if (analyserRef.current && currentState === 'listening') {
      const data = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        sum += data[i];
      }
      const avg = sum / data.length / 255;
      return Math.min(1, avg * 2.5);
    }
    return 0;
  }, [currentState, externalAudioLevel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      canvasSizeRef.current = { w, h };
    };

    const bgImg = new Image();
    bgImg.src = bgImageSrc;
    bgImg.onload = () => {
      bgImageRef.current = bgImg;
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    lastFrameTimeRef.current = performance.now();

    const draw = (timestamp) => {
      const delta = (timestamp - lastFrameTimeRef.current) / 1000;
      lastFrameTimeRef.current = timestamp;
      timeRef.current += delta;

      const rawLevel = getAudioLevel();
      const smoothingFactor = currentState === 'idle' ? 0.03 : 0.12;
      smoothedLevelRef.current = lerp(smoothedLevelRef.current, rawLevel, smoothingFactor);

      const audioLevel = smoothedLevelRef.current;
      const t = timeRef.current;
      const { w, h } = canvasSizeRef.current;
      const dim = Math.min(w, h);
      const cx = w / 2;
      const cy = h / 2;

      if (bgImageRef.current) {
        ctx.drawImage(bgImageRef.current, 0, 0, w, h);
      } else {
        ctx.fillStyle = 'rgb(200, 210, 195)';
        ctx.fillRect(0, 0, w, h);
      }

      const breathCycle1 = easeInOutSine((Math.sin(t * 0.5) + 1) / 2);
      const breathCycle2 = easeInOutSine((Math.sin(t * 0.35 + 1.2) + 1) / 2);
      const idleBreath = breathCycle1 * 0.7 + breathCycle2 * 0.3;
      const idlePulse = idleBreath * 0.38;
      const activePulse = audioLevel * 0.3;
      const pulse = currentState === 'idle' ? idlePulse : activePulse + idlePulse * 0.2;
      const breathAlphaBoost = currentState === 'idle' ? 0.1 + idleBreath * 0.55 : 0;

      const auraBaseRadius = dim * 0.21;
      const auraRadius = auraBaseRadius * (1 + pulse);

      const wobbleStrength = currentState === 'idle' ? 0 : 1 + audioLevel * 2;
      const wobbleX1 = Math.sin(t * 0.7) * dim * 0.008 * wobbleStrength;
      const wobbleY1 = Math.cos(t * 0.6) * dim * 0.006 * wobbleStrength;
      const auraCx = cx + wobbleX1;
      const auraCy = cy + wobbleY1;

      // Outer glow
      const outerGlowRadius = auraRadius * 2.8;
      const outerGlow = ctx.createRadialGradient(auraCx, auraCy, auraRadius * 0.5, auraCx, auraCy, outerGlowRadius);
      const outerBoost = breathAlphaBoost * 0.8;
      outerGlow.addColorStop(0, `rgba(155, 175, 145, ${0.22 + outerBoost})`);
      outerGlow.addColorStop(0.3, `rgba(175, 190, 165, ${0.13 + outerBoost * 0.5})`);
      outerGlow.addColorStop(0.55, `rgba(200, 210, 195, ${0.05 + outerBoost * 0.2})`);
      outerGlow.addColorStop(1, 'rgba(215, 222, 210, 0)');
      ctx.beginPath();
      ctx.arc(auraCx, auraCy, outerGlowRadius, 0, Math.PI * 2);
      ctx.fillStyle = outerGlow;
      ctx.fill();

      // Dark ring
      const darkRingOuter = auraRadius * 1.6;
      const darkRingInner = auraRadius * 0.4;
      const darkRing = ctx.createRadialGradient(auraCx, auraCy, darkRingInner, auraCx, auraCy, darkRingOuter);
      const greenIntensity = 0.7 + audioLevel * 0.3 + breathAlphaBoost * 0.4;
      darkRing.addColorStop(0, `rgba(100, 130, 90, ${0.12 * greenIntensity})`);
      darkRing.addColorStop(0.2, `rgba(120, 148, 108, ${0.38 * greenIntensity})`);
      darkRing.addColorStop(0.4, `rgba(145, 165, 132, ${0.42 * greenIntensity})`);
      darkRing.addColorStop(0.55, `rgba(165, 180, 155, ${0.28 * greenIntensity})`);
      darkRing.addColorStop(0.7, `rgba(185, 198, 178, ${0.12 * greenIntensity})`);
      darkRing.addColorStop(0.85, `rgba(208, 215, 202, ${0.04 * greenIntensity})`);
      darkRing.addColorStop(1, 'rgba(215, 222, 210, 0)');
      ctx.beginPath();
      ctx.arc(auraCx, auraCy, darkRingOuter, 0, Math.PI * 2);
      ctx.fillStyle = darkRing;
      ctx.fill();

      // Bright ring
      const brightRingRadius = auraRadius * 1.2;
      const brightScaleX = 1 + Math.sin(t * 0.9) * 0.03 * (1 + audioLevel * 2);
      const brightScaleY = 1 + Math.cos(t * 0.8) * 0.03 * (1 + audioLevel * 2);
      ctx.save();
      ctx.translate(auraCx, auraCy);
      ctx.scale(brightScaleX, brightScaleY);
      ctx.translate(-auraCx, -auraCy);
      const brightRing = ctx.createRadialGradient(auraCx, auraCy, 0, auraCx, auraCy, brightRingRadius);
      const brightAlpha = 0.5 + audioLevel * 0.3 + breathAlphaBoost * 0.6;
      brightRing.addColorStop(0, `rgba(232, 242, 222, ${brightAlpha * 0.4})`);
      brightRing.addColorStop(0.15, `rgba(238, 246, 228, ${brightAlpha * 0.6})`);
      brightRing.addColorStop(0.32, `rgba(244, 250, 234, ${brightAlpha * 0.85})`);
      brightRing.addColorStop(0.48, `rgba(242, 248, 228, ${brightAlpha})`);
      brightRing.addColorStop(0.65, `rgba(225, 238, 210, ${brightAlpha * 0.55})`);
      brightRing.addColorStop(0.8, `rgba(195, 215, 180, ${brightAlpha * 0.22})`);
      brightRing.addColorStop(0.92, `rgba(172, 195, 158, ${brightAlpha * 0.07})`);
      brightRing.addColorStop(1, 'rgba(160, 185, 148, 0)');
      ctx.beginPath();
      ctx.arc(auraCx, auraCy, brightRingRadius, 0, Math.PI * 2);
      ctx.fillStyle = brightRing;
      ctx.fill();
      ctx.restore();

      // Core glow
      const coreRadius = dim * 0.08 * (1 + pulse * 0.5);
      const coreGlow = ctx.createRadialGradient(auraCx, auraCy, 0, auraCx, auraCy, coreRadius * 3.83);
      const coreAlpha = 0.85 + audioLevel * 0.15 + breathAlphaBoost * 0.5;
      coreGlow.addColorStop(0, `rgba(245, 250, 235, ${coreAlpha * 0.95})`);
      coreGlow.addColorStop(0.12, `rgba(244, 250, 233, ${coreAlpha * 0.92})`);
      coreGlow.addColorStop(0.28, `rgba(240, 247, 228, ${coreAlpha * 0.82})`);
      coreGlow.addColorStop(0.45, `rgba(232, 242, 218, ${coreAlpha * 0.6})`);
      coreGlow.addColorStop(0.62, `rgba(215, 230, 200, ${coreAlpha * 0.35})`);
      coreGlow.addColorStop(0.78, `rgba(195, 215, 182, ${coreAlpha * 0.15})`);
      coreGlow.addColorStop(0.9, `rgba(178, 200, 165, ${coreAlpha * 0.05})`);
      coreGlow.addColorStop(1, 'rgba(165, 188, 152, 0)');
      ctx.beginPath();
      ctx.arc(auraCx, auraCy, coreRadius * 3.83, 0, Math.PI * 2);
      ctx.fillStyle = coreGlow;
      ctx.fill();

      // Expanding rings when active
      if (currentState !== 'idle' && audioLevel > 0.05) {
        const ringCount = 3;
        for (let r = 0; r < ringCount; r++) {
          const ringPhase = (t * 0.35 + r * 0.33) % 1;
          const ringRadius = auraRadius * 0.5 + (auraRadius * 1.2) * ringPhase;
          const ringAlpha = (1 - ringPhase) * 0.12 * audioLevel;
          if (ringAlpha > 0.005) {
            ctx.beginPath();
            ctx.arc(auraCx, auraCy, ringRadius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(195, 215, 182, ${ringAlpha})`;
            ctx.lineWidth = 2 + audioLevel * 3;
            ctx.stroke();
          }
        }
      }

      // Dot wave pattern on audio peaks
      if (currentState !== 'idle' && audioLevel > 0.08 && audioLevel > prevLevelRef.current + 0.015) {
        if (t - lastPeakRef.current > 0.12) {
          dotWavesRef.current.push({ birthTime: t, intensity: Math.min(1, audioLevel * 1.5) });
          lastPeakRef.current = t;
        }
      }
      prevLevelRef.current = audioLevel;
      dotWavesRef.current = dotWavesRef.current.filter((wave) => t - wave.birthTime < 3.5);

      if (dotWavesRef.current.length > 0) {
        const dotSpacing = 16;
        const dotBaseSize = 0.8;
        const cols = Math.ceil(w / dotSpacing) + 1;
        const rows = Math.ceil(h / dotSpacing) + 1;
        const offsetX = (w - (cols - 1) * dotSpacing) / 2;
        const offsetY = (h - (rows - 1) * dotSpacing) / 2;
        const maxWaveDist = Math.sqrt(w * w + h * h) * 0.6;

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const dotX = offsetX + col * dotSpacing;
            const dotY = offsetY + row * dotSpacing;
            const distFromCenter = Math.sqrt(
              (dotX - auraCx) * (dotX - auraCx) + (dotY - auraCy) * (dotY - auraCy)
            );

            let dotAlpha = 0;
            let dotScale = 0;

            for (const wave of dotWavesRef.current) {
              const waveAge = t - wave.birthTime;
              const waveSpeed = maxWaveDist * 0.45;
              const waveFront = waveAge * waveSpeed;
              const waveWidth = dim * 0.12;
              const distFromWave = Math.abs(distFromCenter - waveFront);

              if (distFromWave < waveWidth) {
                const waveT = 1 - distFromWave / waveWidth;
                const fadeOut = Math.max(0, 1 - waveAge / 3.5);
                const contribution = waveT * waveT * fadeOut * wave.intensity;
                dotAlpha = Math.min(0.6, dotAlpha + contribution * 0.5);
                dotScale = Math.max(dotScale, contribution);
              }
            }

            if (dotAlpha > 0.01) {
              const size = dotBaseSize + dotScale * 1.4;
              const rr = Math.round(205 + dotScale * 38);
              const gg = Math.round(222 + dotScale * 22);
              const bb = Math.round(192 + dotScale * 40);
              ctx.beginPath();
              ctx.arc(dotX, dotY, size, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(${rr}, ${gg}, ${bb}, ${dotAlpha})`;
              ctx.fill();
            }
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [currentState, getAudioLevel]);

  useEffect(() => {
    return () => {
      stopMic();
    };
  }, [stopMic]);

  return (
    <div
      ref={containerRef}
      style={fillScreen ? { position: 'fixed', inset: 0 } : { width: '100%', height: '100%' }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
});

VoiceVisualizer.displayName = 'VoiceVisualizer';

export default VoiceVisualizer;
